import React, { useRef, useState, useEffect } from "react";
import JSZip from "jszip";
import { useBranding } from "../lib/branding";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import {
    CloudUpload,
    Loader2,
    Ruler,
    ArrowRight,
    FileText,
    Trash2,
    CheckCircle2,
    AlertTriangle,
    RotateCw,
} from "lucide-react";
import { uploadPlan, updateProject, listUploads, deleteUpload, getUpload } from "../lib/api";
import { toast } from "sonner";

const UNITS_KEY = "planflow.unit_system";

// File types we actually want to ingest. Everything else (DWG, .bak,
// thumbs.db, .DS_Store…) gets silently skipped when expanding a folder
// or zip so a user can drop a whole project directory and not fight it.
const SUPPORTED_EXT_RE = /\.(pdf|png|jpe?g|webp|heic|heif|tiff?|bmp|gif)$/i;

function _isSupportedFile(name) {
    return SUPPORTED_EXT_RE.test(name || "");
}

/** Walk a DataTransferItemList entry tree (webkitGetAsEntry) and yield every
 *  leaf File we recognise. Silently skips unsupported types and hidden dotfiles. */
async function _filesFromEntry(entry, pathPrefix = "") {
    if (!entry) return [];
    if (entry.isFile) {
        // `file()` is async; also it throws on some browsers for 0-byte files.
        const file = await new Promise((resolve, reject) =>
            entry.file(resolve, reject),
        );
        if (!file || file.size === 0) return [];
        // Skip Mac / Windows noise files.
        if (/^\.|^Thumbs\.db$|^__MACOSX/i.test(file.name)) return [];
        if (!_isSupportedFile(file.name)) return [];
        // Preserve the relative path so duplicate "Page 1.pdf" across folders
        // don't collide in the UI list. Browsers don't give us File.name with
        // the folder prefix by default.
        const pretty = pathPrefix ? `${pathPrefix}/${file.name}` : file.name;
        try {
            Object.defineProperty(file, "name", { value: pretty, configurable: true });
        } catch {
            /* some browsers make File.name read-only — ignore, uniqueness drops */
        }
        return [file];
    }
    if (entry.isDirectory) {
        const reader = entry.createReader();
        const children = await new Promise((resolve) => {
            // readEntries may only return a batch at a time on Chrome — loop.
            const out = [];
            const readBatch = () =>
                reader.readEntries((batch) => {
                    if (!batch || batch.length === 0) return resolve(out);
                    out.push(...batch);
                    readBatch();
                });
            readBatch();
        });
        const nested = await Promise.all(
            children.map((c) =>
                _filesFromEntry(
                    c,
                    pathPrefix ? `${pathPrefix}/${entry.name}` : entry.name,
                ),
            ),
        );
        return nested.flat();
    }
    return [];
}

/** Expand a zip File object into an array of inner File objects (PDFs /
 *  images only). Non-recursive but JSZip handles deep trees natively. */
async function _filesFromZip(zipFile) {
    const zip = await JSZip.loadAsync(zipFile);
    const out = [];
    const entries = Object.values(zip.files);
    for (const entry of entries) {
        if (entry.dir) continue;
        if (/^__MACOSX\//.test(entry.name)) continue;
        if (!_isSupportedFile(entry.name)) continue;
        const blob = await entry.async("blob");
        if (!blob || blob.size === 0) continue;
        // Infer a sensible MIME from the extension.
        const ext = (entry.name.split(".").pop() || "").toLowerCase();
        const type =
            ext === "pdf"
                ? "application/pdf"
                : ext === "png"
                ? "image/png"
                : ext === "jpg" || ext === "jpeg"
                ? "image/jpeg"
                : ext === "webp"
                ? "image/webp"
                : blob.type || "application/octet-stream";
        // Keep the folder prefix in the name so 10 "A1.pdf"s don't collide.
        const pretty = entry.name.split("/").slice(-2).join("/");
        out.push(new File([blob], pretty, { type, lastModified: Date.now() }));
    }
    return out;
}

function readStoredUnitSystem() {
    try {
        return localStorage.getItem(UNITS_KEY) || "imperial";
    } catch {
        return "imperial";
    }
}

function writeStoredUnitSystem(value) {
    try {
        localStorage.setItem(UNITS_KEY, value);
    } catch {
        /* noop */
    }
}

function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export const UploadPlansDialog = ({
    open,
    onOpenChange,
    onUploaded,
    onDeleted,
    projectId,
    onCreated,
}) => {
    const [dragging, setDragging] = useState(false);
    const [unitSystem, setUnitSystem] = useState(readStoredUnitSystem());
    const [submitting, setSubmitting] = useState(false);
    const [files, setFiles] = useState([]);
    // items = in-progress or recently completed uploads, keyed by local id.
    // { localId, name, size, progress (0-100), status: "uploading"|"done"|"error", record? }
    const [items, setItems] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);

    // iter-62: let the superadmin override the uploading-video with a
    // branded clip; fall back to the bundled default.
    const { videoUploadUrl } = useBranding();
    const heroVideoSrc = videoUploadUrl
        ? videoUploadUrl.startsWith("http")
            ? videoUploadUrl
            : `${process.env.REACT_APP_BACKEND_URL}${videoUploadUrl}`
        : "/uploading.mp4";

    const isUploading = items.some(
        (it) =>
            it.status === "uploading" ||
            (it.status === "done" &&
                it.split_status &&
                it.split_status !== "done" &&
                it.split_status !== "error"),
    );

    useEffect(() => {
        if (!open) return;
        setUnitSystem(readStoredUnitSystem());
        setFiles([]);
        setItems([]);

        // Silently pre-cache the upload video so it can play immediately the
        // moment a file is dropped (no display-time hold, just cache-warm).
        const v = document.createElement("video");
        v.src = heroVideoSrc;
        v.preload = "auto";
        v.muted = true;
        try { v.load(); } catch {}

        if (!projectId) return;
        setLoadingList(true);
        listUploads(projectId)
            .then((docs) => setFiles(docs || []))
            .catch(() => toast.error("Couldn't load existing files"))
            .finally(() => setLoadingList(false));
    }, [open, projectId]);

    // Poll a parent upload's split_status until the backend finishes extracting
    // per-page children, so we can render a live "Splitting X / Y" bar.
    const pollSplit = (localId, uploadId) => {
        let alive = true;
        const tick = async () => {
            if (!alive) return;
            try {
                const u = await getUpload(uploadId);
                setItems((prev) =>
                    prev.map((it) =>
                        it.localId === localId
                            ? {
                                  ...it,
                                  split_status: u.split_status,
                                  split_done_pages: u.split_done_pages,
                                  split_page_count: u.page_count,
                              }
                            : it,
                    ),
                );
                if (u.split_status === "done" || u.split_status === "error") {
                    alive = false;
                    return;
                }
            } catch {
                // transient error — keep trying
            }
            setTimeout(tick, 1000);
        };
        tick();
    };

    const retryFailed = async (localId) => {
        const row = items.find((it) => it.localId === localId);
        if (!row || !row.file) return;
        setItems((prev) =>
            prev.map((it) =>
                it.localId === localId
                    ? { ...it, status: "uploading", progress: 0 }
                    : it,
            ),
        );
        try {
            const record = await uploadPlan(row.file, projectId, (pct) => {
                setItems((prev) =>
                    prev.map((it) =>
                        it.localId === localId ? { ...it, progress: pct } : it,
                    ),
                );
            });
            setItems((prev) =>
                prev.map((it) =>
                    it.localId === localId
                        ? { ...it, progress: 100, status: "done", record }
                        : it,
                ),
            );
            setFiles((prev) => [record, ...prev]);
            onUploaded && onUploaded(record);
            if (record.page_count > 1) pollSplit(localId, record.id);
        } catch (e) {
            setItems((prev) =>
                prev.map((it) =>
                    it.localId === localId ? { ...it, status: "error" } : it,
                ),
            );
            toast.error(`Retry failed: ${row.name}`);
        }
    };

    const handleFiles = async (picked) => {
        if (!picked || picked.length === 0) return;

        // Expand any zip archives inline so a user can drop a single
        // project-bundle.zip and get all its plans queued up. Walk sequentially
        // so we can surface a per-zip toast with the count.
        const expanded = [];
        for (const f of picked) {
            const looksLikeZip =
                (f.name || "").toLowerCase().endsWith(".zip") ||
                f.type === "application/zip" ||
                f.type === "application/x-zip-compressed";
            if (looksLikeZip) {
                const t = toast.loading(`Opening ${f.name}…`);
                try {
                    const inner = await _filesFromZip(f);
                    toast.dismiss(t);
                    if (inner.length === 0) {
                        toast.error(`${f.name}: no plans found inside`);
                    } else {
                        toast.success(
                            `${f.name}: ${inner.length} plan${inner.length === 1 ? "" : "s"} queued`,
                        );
                    }
                    expanded.push(...inner);
                } catch (e) {
                    toast.dismiss(t);
                    toast.error(`Couldn't read ${f.name} — is it a valid .zip?`);
                }
            } else {
                // Drop unsupported types silently (aligns with the folder
                // walker's behaviour; user got instant feedback via the
                // picker's `accept` attribute already).
                if (_isSupportedFile(f.name) || !f.name.includes(".")) {
                    expanded.push(f);
                }
            }
        }

        if (expanded.length === 0) {
            toast.error("No supported files (PDF or image) in this drop.");
            return;
        }

        // Seed each expanded file as an uploading item immediately. Keep the
        // File object for retry without re-picking.
        const seeded = expanded.map((f) => ({
            localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name: f.name,
            size: f.size,
            progress: 0,
            status: "uploading",
            file: f,
        }));
        setItems((prev) => [...seeded, ...prev]);

        // Upload sequentially so the network doesn't saturate and progress
        // bars advance one-at-a-time for a clear visual. Each upload already
        // uses chunked-resumable transport for anything >4 MB.
        for (let i = 0; i < expanded.length; i++) {
            const file = expanded[i];
            const { localId } = seeded[i];
            try {
                const record = await uploadPlan(file, projectId, (pct) => {
                    setItems((prev) =>
                        prev.map((it) =>
                            it.localId === localId ? { ...it, progress: pct } : it,
                        ),
                    );
                });
                setItems((prev) =>
                    prev.map((it) =>
                        it.localId === localId
                            ? { ...it, progress: 100, status: "done", record }
                            : it,
                    ),
                );
                setFiles((prev) => [record, ...prev]);
                onUploaded && onUploaded(record);
                if (record.page_count > 1) pollSplit(localId, record.id);
            } catch (e) {
                setItems((prev) =>
                    prev.map((it) =>
                        it.localId === localId ? { ...it, status: "error" } : it,
                    ),
                );
                toast.error(`Failed: ${file.name}`);
            }
        }
    };

    const onDrop = async (e) => {
        e.preventDefault();
        setDragging(false);
        // Prefer DataTransferItemList.webkitGetAsEntry so we get folder support
        // (dropping a whole project folder walks the tree). Falls back to
        // `dataTransfer.files` when the browser doesn't expose entries.
        const items = e.dataTransfer?.items;
        if (items && items.length > 0 && items[0].webkitGetAsEntry) {
            const entries = [];
            for (let i = 0; i < items.length; i++) {
                const entry = items[i].webkitGetAsEntry && items[i].webkitGetAsEntry();
                if (entry) entries.push(entry);
                else if (items[i].kind === "file") {
                    const f = items[i].getAsFile();
                    if (f) entries.push({ _plainFile: f });
                }
            }
            const nested = await Promise.all(
                entries.map((ent) =>
                    ent && ent._plainFile
                        ? Promise.resolve([ent._plainFile])
                        : _filesFromEntry(ent),
                ),
            );
            return handleFiles(nested.flat());
        }
        handleFiles(Array.from(e.dataTransfer.files || []));
    };

    const pickUnits = (next) => {
        setUnitSystem(next);
        writeStoredUnitSystem(next);
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
            await deleteUpload(pendingDelete.id);
            setFiles((prev) => prev.filter((f) => f.id !== pendingDelete.id));
            setItems((prev) =>
                prev.filter((it) => !it.record || it.record.id !== pendingDelete.id),
            );
            onDeleted && onDeleted(pendingDelete.id);
            toast.success("File deleted");
            setPendingDelete(null);
        } catch (e) {
            toast.error("Couldn't delete file");
        } finally {
            setDeleting(false);
        }
    };

    const handleCreate = async () => {
        setSubmitting(true);
        try {
            writeStoredUnitSystem(unitSystem);
            if (projectId) {
                await updateProject(projectId, { unit_system: unitSystem });
            }
            onCreated && onCreated({ projectId, unitSystem });
            onOpenChange(false);
        } catch (e) {
            toast.error("Couldn't finalize project");
        } finally {
            setSubmitting(false);
        }
    };

    // Merge in-progress items with server-stored records, de-duped by record.id
    const mergedList = (() => {
        const seen = new Set();
        const rows = [];
        for (const it of items) {
            const id = it.record?.id || `live-${it.localId}`;
            seen.add(id);
            rows.push({
                key: id,
                localId: it.localId,
                name: it.name,
                size: it.size,
                progress: it.progress,
                status: it.status,
                record: it.record,
                split_status: it.split_status,
                split_done_pages: it.split_done_pages,
                split_page_count: it.split_page_count,
            });
        }
        for (const f of files) {
            if (seen.has(f.id)) continue;
            rows.push({
                key: f.id,
                name: f.original_filename,
                size: f.size,
                progress: 100,
                status: "stored",
                record: f,
                // If the backend is still splitting this stored file (e.g.
                // the user re-opened the dialog before split finished), show
                // progress too.
                split_status: f.split_status,
                split_done_pages: f.split_done_pages,
                split_page_count: f.page_count,
            });
        }
        return rows;
    })();

    return (
        <>
            <Dialog open={open} onOpenChange={(v) => !isUploading && onOpenChange(v)}>
                <DialogContent
                    className="bg-[#131A2F] border border-[#2A3655] text-white w-[calc(100vw-1.5rem)] max-w-[560px] max-h-[85vh] overflow-hidden p-4 sm:p-6"
                    data-testid="upload-plans-dialog"
                >
                    <div className="min-w-0 overflow-y-auto max-h-[calc(85vh-2rem)] flex flex-col gap-4">
                        <DialogHeader className="min-w-0">
                            <DialogTitle className="text-white">Upload plans</DialogTitle>
                            <DialogDescription className="text-slate-400">
                                Drop PDF blueprints or images and pick your unit system.
                            </DialogDescription>
                        </DialogHeader>

                        {isUploading && (
                            <div
                                data-testid="upload-in-progress"
                                className="sticky top-0 z-10 shrink-0 rounded-md border-2 border-[#00A3FF]/40 bg-[#0A1128] overflow-hidden h-[180px] shadow-lg shadow-black/30"
                            >
                                <video
                                    key={heroVideoSrc}
                                    src={heroVideoSrc}
                                    className="absolute inset-0 w-full h-full object-cover opacity-90"
                                    muted
                                    autoPlay
                                    loop
                                    playsInline
                                    preload="auto"
                                    data-testid="upload-video"
                                />
                                <div className="absolute inset-0 flex items-end p-3 bg-gradient-to-t from-[#0A1128]/90 via-[#0A1128]/30 to-transparent">
                                    <div className="flex items-center gap-2 text-white">
                                        <Loader2 className="w-4 h-4 animate-spin text-[#00A3FF]" />
                                        <span className="text-sm font-semibold">
                                            Uploading {items.filter((i) => i.status === "uploading").length}{" "}
                                            of {items.filter((i) => i.status === "uploading" || i.status === "done" || i.status === "error").length}
                                            …
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!isUploading && (
                            <div
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setDragging(true);
                                }}
                                onDragLeave={() => setDragging(false)}
                                onDrop={onDrop}
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="upload-dropzone"
                                className={`cursor-pointer rounded-md border-2 border-dashed p-5 text-center transition-colors min-w-0 ${
                                    dragging
                                        ? "border-[#00A3FF] bg-[#007AFF]/10"
                                        : "border-[#2A3655] hover:border-[#3A4A75] bg-[#0A1128]"
                                }`}
                            >
                                <div className="flex flex-col items-center gap-2 min-w-0">
                                    <div className="w-12 h-12 rounded-full bg-[#1C253C] border border-[#2A3655] flex items-center justify-center text-[#00A3FF]">
                                        <CloudUpload className="w-6 h-6" />
                                    </div>
                                    <div className="font-medium text-white text-sm sm:text-base">
                                        {mergedList.length > 0
                                            ? "Drop more or tap to add"
                                            : "Drop files or tap to browse"}
                                    </div>
                                    <div className="text-xs text-slate-400 break-words">
                                        PDF, PNG, JPG · drop a .zip or a whole folder — we'll expand it
                                    </div>
                                    {/* iter-167 — hide the folder picker on touch
                                        devices. Android's folder tree picker
                                        forces "USE THIS FOLDER" (no per-file
                                        selection) and users kept fat-fingering
                                        this 11px link on tablets. Desktop
                                        (mouse) users still get it. */}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            folderInputRef.current?.click();
                                        }}
                                        data-testid="upload-pick-folder"
                                        className="mt-2 text-[11px] font-semibold text-[#00A3FF] hover:text-white underline underline-offset-2 hidden md:[@media(hover:hover)]:inline"
                                    >
                                        Or pick a folder
                                    </button>
                                </div>
                            </div>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.tiff,.tif,.bmp,.gif,.zip,application/pdf,application/zip,image/*"
                            className="hidden"
                            data-testid="upload-file-input"
                            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                        />
                        {/* Separate hidden input with webkitdirectory so the
                            "Pick folder" button lets the user hand us a whole
                            project tree. Chrome/Edge/Safari support this. */}
                        <input
                            ref={folderInputRef}
                            type="file"
                            webkitdirectory=""
                            directory=""
                            multiple
                            className="hidden"
                            data-testid="upload-folder-input"
                            onChange={async (e) => {
                                const list = Array.from(e.target.files || []);
                                const keep = list.filter((f) => _isSupportedFile(f.name));
                                if (keep.length === 0)
                                    toast.error("No PDFs or images in that folder.");
                                else handleFiles(keep);
                            }}
                        />

                        {(mergedList.length > 0 || loadingList) && (
                            <div className="min-w-0" data-testid="uploaded-files-section">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs uppercase tracking-[0.15em] font-bold text-slate-300">
                                        Files ({mergedList.length})
                                    </span>
                                    {loadingList && (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />
                                    )}
                                </div>
                                <ul
                                    className="divide-y divide-[#2A3655] rounded-md border border-[#2A3655] bg-[#0A1128] max-h-56 overflow-y-auto"
                                    data-testid="uploaded-files-list"
                                >
                                    {mergedList.map((row) => (
                                        <FileRow
                                            key={row.key}
                                            row={row}
                                            onDelete={() => row.record && setPendingDelete(row.record)}
                                            onRetry={() => retryFailed(row.localId)}
                                        />
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="min-w-0" data-testid="unit-system-section">
                            <div className="flex items-center gap-2 mb-2">
                                <Ruler className="w-4 h-4 text-[#00A3FF]" />
                                <span className="text-xs uppercase tracking-[0.15em] font-bold text-slate-300">
                                    Unit system
                                </span>
                            </div>
                            <div
                                className="grid grid-cols-2 gap-2 p-1 bg-[#0A1128] border border-[#2A3655] rounded-md"
                                role="radiogroup"
                                aria-label="Unit system"
                            >
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={unitSystem === "imperial"}
                                    onClick={() => pickUnits("imperial")}
                                    data-testid="unit-toggle-imperial"
                                    className={`py-2.5 rounded-sm text-sm font-semibold transition-colors ${
                                        unitSystem === "imperial"
                                            ? "bg-[#007AFF] text-white shadow-[0_0_14px_rgba(0,122,255,0.3)]"
                                            : "text-slate-300 hover:text-white hover:bg-[#1C253C]"
                                    }`}
                                >
                                    Imperial
                                    <span className="block text-[10px] font-normal opacity-80 mt-0.5">
                                        ft · in · lb
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={unitSystem === "metric"}
                                    onClick={() => pickUnits("metric")}
                                    data-testid="unit-toggle-metric"
                                    className={`py-2.5 rounded-sm text-sm font-semibold transition-colors ${
                                        unitSystem === "metric"
                                            ? "bg-[#007AFF] text-white shadow-[0_0_14px_rgba(0,122,255,0.3)]"
                                            : "text-slate-300 hover:text-white hover:bg-[#1C253C]"
                                    }`}
                                >
                                    Metric
                                    <span className="block text-[10px] font-normal opacity-80 mt-0.5">
                                        m · cm · kg
                                    </span>
                                </button>
                            </div>
                        </div>

                        <DialogFooter className="min-w-0 gap-2 sm:gap-2">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                disabled={isUploading}
                                data-testid="btn-cancel-upload"
                                className="text-slate-300 hover:text-white hover:bg-[#1C253C] disabled:opacity-40"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                onClick={handleCreate}
                                disabled={submitting || isUploading}
                                data-testid="btn-create-project"
                                className="bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold disabled:opacity-40 disabled:bg-[#2A3655] disabled:text-slate-400"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        Done
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={!!pendingDelete}
                onOpenChange={(v) => !v && !deleting && setPendingDelete(null)}
            >
                <AlertDialogContent
                    className="bg-[#131A2F] border border-[#2A3655] text-white"
                    data-testid="confirm-delete-upload-dialog"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                            Delete this file?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400 break-words">
                            <span className="text-slate-200 font-medium">
                                {pendingDelete?.original_filename}
                            </span>{" "}
                            will be removed from this project. This can't be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="btn-cancel-delete-upload"
                            disabled={deleting}
                            className="bg-transparent text-slate-300 border-[#2A3655] hover:bg-[#1C253C] hover:text-white"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            disabled={deleting}
                            data-testid="btn-confirm-delete-upload"
                            className="bg-red-600 hover:bg-red-500 text-white font-bold"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Deleting…
                                </>
                            ) : (
                                "Delete"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

function FileRow({ row, onDelete, onRetry }) {
    const uploading = row.status === "uploading";
    const error = row.status === "error";
    const done = row.status === "done";
    const stored = row.status === "stored";

    return (
        <li
            className="px-3 py-2 min-w-0"
            data-testid={`uploaded-file-${row.key}`}
            data-status={row.status}
        >
            <div className="flex items-center gap-2 min-w-0">
                {error ? (
                    <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                ) : done ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                ) : (
                    <FileText className="w-4 h-4 text-[#00A3FF] shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                    <div className="text-sm text-white truncate" title={row.name}>
                        {row.name}
                    </div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                        {formatBytes(row.size)}
                        {uploading && ` · ${row.progress}%`}
                        {done && " · Uploaded"}
                        {error && " · Failed"}
                    </div>
                </div>
                {stored || done ? (
                    <button
                        type="button"
                        onClick={onDelete}
                        data-testid={`delete-upload-${row.key}`}
                        aria-label={`Delete ${row.name}`}
                        className="shrink-0 w-8 h-8 rounded-sm flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 transition-colors active:scale-95"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                ) : error && onRetry ? (
                    <button
                        type="button"
                        onClick={onRetry}
                        data-testid={`retry-upload-${row.key}`}
                        aria-label={`Retry upload of ${row.name}`}
                        className="shrink-0 w-8 h-8 rounded-sm flex items-center justify-center text-[#00A3FF] hover:bg-[#00A3FF]/10 border border-transparent hover:border-[#00A3FF]/30 transition-colors active:scale-95"
                        title="Retry upload"
                    >
                        <RotateCw className="w-4 h-4" />
                    </button>
                ) : (
                    <div className="shrink-0 w-8 h-8 flex items-center justify-center">
                        {uploading && (
                            <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                        )}
                    </div>
                )}
            </div>
            {/* Progress bar: green throughout — matches the "per-process"
                green progress pattern the user asked for. Red on error. */}
            {(uploading || done || error) && (
                <div className="mt-1.5 h-1 w-full rounded-full bg-[#131A2F] overflow-hidden">
                    <div
                        className={`h-full transition-[width,background-color] duration-200 ease-out ${
                            error
                                ? "bg-red-500"
                                : "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]"
                        }`}
                        style={{ width: `${error ? 100 : row.progress}%` }}
                    />
                </div>
            )}
            {/* Split progress bar: appears only for multi-page PDFs while the
                 backend is extracting per-page children. */}
            {row.split_status &&
                row.split_status !== "done" &&
                row.split_status !== "error" &&
                (row.split_page_count || 0) > 1 && (
                    <div
                        className="mt-1 text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center justify-between"
                        data-testid={`split-progress-${row.key}`}
                    >
                        <span className="inline-flex items-center gap-1 text-[#00A3FF]">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Splitting pages…
                        </span>
                        <span className="tabular-nums text-slate-300">
                            {row.split_done_pages || 0} / {row.split_page_count}
                        </span>
                    </div>
                )}
            {row.split_status &&
                row.split_status !== "done" &&
                row.split_status !== "error" &&
                (row.split_page_count || 0) > 1 && (
                    <div className="mt-1 h-1 w-full rounded-full bg-[#131A2F] overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#00A3FF] to-[#3395FF] transition-[width] duration-200 ease-out"
                            style={{
                                width: `${Math.min(
                                    100,
                                    ((row.split_done_pages || 0) /
                                        Math.max(1, row.split_page_count)) *
                                        100,
                                )}%`,
                            }}
                        />
                    </div>
                )}
            {row.split_status === "done" && (row.split_page_count || 0) > 1 && (
                <div
                    className="mt-1 text-[10px] uppercase tracking-wider text-emerald-400 font-bold flex items-center gap-1"
                    data-testid={`split-done-${row.key}`}
                >
                    <CheckCircle2 className="w-3 h-3" />
                    Split into {row.split_page_count} pages
                </div>
            )}
        </li>
    );
}

export default UploadPlansDialog;
